// Frontend IRC chat monitoring using WebSocket
// One anonymous (justinfan) connection joins every monitored channel, counts
// PRIVMSGs per channel, and exposes 10s / 5m rates. Survives disconnects:
// exponential-backoff reconnect, JOIN rate-limit queue, activity watchdog.
//
// Counting lives in RateWindow — a per-second ring buffer per channel — so a
// message costs O(1) and reading every channel's rates costs the same whether
// the chat does 1 or 100 messages a second.

import { parseIrcFrame } from './parseIrc';
import { RateWindow, SHORT_S } from './rateWindow';

export interface ChatMetrics {
  messageCount: number;
  chatRate: number;
  chatRate10s: number;
  chatRate5m: number;
  /** Seconds of observation backing chatRate5m (capped at 300). Heat ratios
   *  are only meaningful once this passes the baseline (see utils/heat) —
   *  before that the short denominator inflates 10s/5m for every channel. */
  sampleSeconds: number;
  lastUpdate: number;
}

// Spam filter settings
export interface SpamFilterSettings {
  filterVotes: boolean;
  filterCommands: boolean;
}

function isSpamMessage(content: string, settings: SpamFilterSettings): boolean {
  const trimmed = content.trim();
  if (settings.filterVotes && /^[1-9]$/.test(trimmed)) return true;
  if (settings.filterCommands && /^![\w]+$/.test(trimmed)) return true;
  return false;
}

type MetricsCallback = (metrics: Map<string, ChatMetrics>) => void;
type StatusCallback = (connected: boolean) => void;

const IRC_URL = 'wss://irc-ws.chat.twitch.tv:443';
// Twitch allows ~20 JOINs per 10s — stay under it or joins get silently dropped
const JOIN_BURST = 100;
const JOIN_WINDOW_MS = 5000;
const RECONNECT_MAX_MS = 30_000;
// Twitch pings roughly every 5 minutes; silence beyond that means a dead socket
const IDLE_PING_MS = 5.5 * 60 * 1000;
const IDLE_DEAD_MS = 7 * 60 * 1000;
/** A 5-minute average needs at least this much time under it to mean anything */
const MIN_AVERAGE_WINDOW_S = 30;
/** History resolution for sparklines: 60 buckets of 5 seconds = the 5m window */
export const HISTORY_BUCKET_S = 5;
export const HISTORY_BUCKETS = 60;

export class ChatMonitor {
  private ws: WebSocket | null = null;
  private channels: Set<string> = new Set();
  private windows: Map<string, RateWindow> = new Map();
  private updateInterval: ReturnType<typeof setInterval> | null = null;
  private onMetricsUpdate: MetricsCallback | null = null;
  private onStatusUpdate: StatusCallback | null = null;
  private maxChannels = 140;
  private priorityChannels: Set<string> = new Set(); // followed + custom streams
  private spamFilter: SpamFilterSettings = { filterVotes: true, filterCommands: true };

  private shouldRun = false;
  private connected = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdogInterval: ReturnType<typeof setInterval> | null = null;
  private lastActivity = 0;

  private joinQueue: string[] = [];
  private joinTimestamps: number[] = []; // recent JOIN sends for rate limiting
  private joinTimer: ReturnType<typeof setTimeout> | null = null;

  setSpamFilter(settings: SpamFilterSettings) {
    this.spamFilter = settings;
  }

  /** Optional push feed of every channel's metrics once a second */
  setMetricsCallback(callback: MetricsCallback) {
    this.onMetricsUpdate = callback;
    this.startMetricsInterval();
  }

  onStatusChange(callback: StatusCallback) {
    this.onStatusUpdate = callback;
    callback(this.connected);
  }

  isConnected(): boolean {
    return this.connected;
  }

  private setConnected(connected: boolean) {
    if (this.connected === connected) return;
    this.connected = connected;
    this.onStatusUpdate?.(connected);
  }

  private startMetricsInterval() {
    if (this.updateInterval || !this.onMetricsUpdate) return;

    this.updateInterval = setInterval(() => {
      if (this.onMetricsUpdate) {
        this.onMetricsUpdate(this.getAllMetrics());
      }
    }, 1000);
  }

  private computeMetric(window: RateWindow, now: number): ChatMetrics {
    const m = window.metrics(now);
    // Use actual elapsed time for the 5m rate (capped at 5 minutes), and at
    // least 30 seconds of data for a meaningful average
    const effectiveWindow = Math.max(m.elapsedSeconds, MIN_AVERAGE_WINDOW_S);
    const rate10s = m.count10s / SHORT_S;

    return {
      chatRate: rate10s,
      messageCount: m.messageCount,
      chatRate10s: rate10s,
      chatRate5m: m.count5m / effectiveWindow,
      sampleSeconds: m.elapsedSeconds,
      lastUpdate: now,
    };
  }

  getAllMetrics(): Map<string, ChatMetrics> {
    const now = Date.now();
    const result = new Map<string, ChatMetrics>();
    this.windows.forEach((window, channel) => {
      result.set(channel, this.computeMetric(window, now));
    });
    return result;
  }

  getMetrics(channelName: string): ChatMetrics | undefined {
    const window = this.windows.get(channelName.toLowerCase());
    if (!window) return undefined;
    return this.computeMetric(window, Date.now());
  }

  getChatRate(channelName: string): number {
    return this.getMetrics(channelName)?.chatRate || 0;
  }

  /** Messages per 5-second bucket over the last five minutes, oldest first;
   *  empty for a channel the monitor has not joined. */
  getHistory(channelName: string): number[] {
    const window = this.windows.get(channelName.toLowerCase());
    if (!window) return [];
    return window.history(Date.now(), HISTORY_BUCKET_S, HISTORY_BUCKETS);
  }

  // ————— connection lifecycle —————

  private connect() {
    if (!this.shouldRun) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const ws = new WebSocket(IRC_URL);
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws || ws.readyState !== WebSocket.OPEN) return;
      // Anonymous connection - no auth needed for read-only
      ws.send('CAP REQ :twitch.tv/tags twitch.tv/commands');
      ws.send('PASS SCHMOOPIIE');
      ws.send('NICK justinfan' + Math.floor(Math.random() * 100000));
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      const now = Date.now();
      this.lastActivity = now;

      for (const ircEvent of parseIrcFrame(event.data as string)) {
        switch (ircEvent.type) {
          case 'ping':
            ws.send(`PONG :${ircEvent.payload || 'tmi.twitch.tv'}`);
            break;
          case 'welcome':
            this.setConnected(true);
            this.reconnectAttempts = 0;
            // (Re)join everything we're supposed to be monitoring
            this.joinQueue = Array.from(this.channels);
            this.drainJoinQueue();
            break;
          case 'reconnect':
            // Twitch is restarting this edge server — reconnect proactively
            try { ws.close(); } catch {}
            break;
          case 'privmsg': {
            const window = this.windows.get(ircEvent.channel);
            if (window && !isSpamMessage(ircEvent.content, this.spamFilter)) {
              window.record(now);
            }
            break;
          }
        }
      }
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.setConnected(false);
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose follows and handles the reconnect
    };

    this.startWatchdog();
  }

  private scheduleReconnect() {
    if (!this.shouldRun || this.reconnectTimer) return;
    const backoff = Math.min(RECONNECT_MAX_MS, 1000 * 2 ** this.reconnectAttempts);
    const delay = backoff + Math.random() * 500;
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startWatchdog() {
    if (this.watchdogInterval) return;
    this.watchdogInterval = setInterval(() => {
      if (!this.shouldRun || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      const idle = Date.now() - this.lastActivity;
      if (idle > IDLE_DEAD_MS) {
        // No traffic and no PONG — assume dead and force a reconnect
        try { this.ws.close(); } catch {}
      } else if (idle > IDLE_PING_MS) {
        try { this.ws.send('PING :keepalive'); } catch {}
      }
    }, 60 * 1000);
  }

  // ————— join queue (rate limited) —————

  private drainJoinQueue() {
    if (this.joinTimer) {
      clearTimeout(this.joinTimer);
      this.joinTimer = null;
    }
    if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const now = Date.now();
    this.joinTimestamps = this.joinTimestamps.filter(t => now - t < JOIN_WINDOW_MS);

    while (this.joinQueue.length > 0 && this.joinTimestamps.length < JOIN_BURST) {
      const channel = this.joinQueue.shift()!;
      if (!this.channels.has(channel)) continue; // stopped while queued
      this.ws.send(`JOIN #${channel}`);
      this.joinTimestamps.push(Date.now());
      // A channel that never got traffic starts its 5m window at actual join time
      this.windows.get(channel)?.restartIfSilent(Date.now());
    }

    if (this.joinQueue.length > 0) {
      const oldest = this.joinTimestamps[0] || now;
      const wait = Math.max(250, JOIN_WINDOW_MS - (now - oldest) + 50);
      this.joinTimer = setTimeout(() => {
        this.joinTimer = null;
        this.drainJoinQueue();
      }, wait);
    }
  }

  private requestJoin(channel: string) {
    if (!this.joinQueue.includes(channel)) {
      this.joinQueue.push(channel);
    }
    this.drainJoinQueue();
  }

  // ————— public monitoring API —————

  async startMonitoring(channelName: string, isPriority: boolean = false): Promise<void> {
    const channel = channelName.toLowerCase().trim();
    if (!channel) return;

    if (this.channels.has(channel)) {
      if (isPriority) {
        this.priorityChannels.add(channel);
      }
      return;
    }

    // If at max, try to kick a non-priority channel for a priority one
    if (this.channels.size >= this.maxChannels) {
      if (isPriority) {
        const nonPriorityChannel = Array.from(this.channels).find(c => !this.priorityChannels.has(c));
        if (nonPriorityChannel) {
          this.stopMonitoring(nonPriorityChannel);
        } else {
          return; // All channels are priority, can't add more
        }
      } else {
        return; // Non-priority channel and at max
      }
    }

    if (isPriority) {
      this.priorityChannels.add(channel);
    }

    this.channels.add(channel);
    this.windows.set(channel, new RateWindow(Date.now()));

    this.shouldRun = true;
    this.startMetricsInterval(); // re-arm after a stopAll() (e.g. StrictMode remount)
    this.connect();
    this.requestJoin(channel);
  }

  stopMonitoring(channelName: string): void {
    const channel = channelName.toLowerCase();

    if (!this.channels.has(channel)) {
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      try { this.ws.send(`PART #${channel}`); } catch {}
    }

    this.channels.delete(channel);
    this.windows.delete(channel);
    this.priorityChannels.delete(channel);
    this.joinQueue = this.joinQueue.filter(c => c !== channel);
  }

  /**
   * Make the non-priority (top pool) side of the monitor match `channels`.
   * Stale non-priority channels are parted so a new game/language pool never
   * collides with the 140-channel cap; priority channels are untouched.
   */
  async syncBaseChannels(channels: string[]): Promise<void> {
    const target = new Set(
      channels.map(c => c.toLowerCase().trim()).filter(Boolean)
    );

    Array.from(this.channels).forEach(channel => {
      if (!this.priorityChannels.has(channel) && !target.has(channel)) {
        this.stopMonitoring(channel);
      }
    });

    for (const channel of Array.from(target)) {
      await this.startMonitoring(channel, false);
    }
  }

  /**
   * Drop priority status from channels no longer followed/listed. They keep
   * streaming counts until the next base-pool sync parts them if unneeded.
   */
  demotePriorityExcept(keep: string[]): void {
    const keepSet = new Set(keep.map(c => c.toLowerCase().trim()));
    Array.from(this.priorityChannels).forEach(channel => {
      if (!keepSet.has(channel)) {
        this.priorityChannels.delete(channel);
      }
    });
  }

  async updateChannels(newChannels: string[]): Promise<void> {
    const newChannelSet = new Set(newChannels.map(c => c.toLowerCase()));

    // Part from channels no longer in list
    Array.from(this.channels).forEach(channel => {
      if (!newChannelSet.has(channel)) {
        this.stopMonitoring(channel);
      }
    });

    // Join new channels
    for (const channel of Array.from(newChannelSet)) {
      await this.startMonitoring(channel, false);
    }
  }

  stopAll(): void {
    this.shouldRun = false;

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.watchdogInterval) {
      clearInterval(this.watchdogInterval);
      this.watchdogInterval = null;
    }
    if (this.joinTimer) {
      clearTimeout(this.joinTimer);
      this.joinTimer = null;
    }

    if (this.ws) {
      const ws = this.ws;
      this.ws = null; // detach handlers' self-checks before closing
      try { ws.close(); } catch {}
    }

    this.channels.clear();
    this.windows.clear();
    this.priorityChannels.clear();
    this.joinQueue = [];
    this.joinTimestamps = [];
    this.setConnected(false);
  }
}

// Singleton instance
export const chatMonitor = new ChatMonitor();
